<template>
  <div v-if="patient" class="row">
    <div
      :class="!staticGrid ? 'col-auto col-lg-12' : 'col-12'"
      class="d-flex justify-content-center"
    >
      <div class="d-inline-block img-thumbnail mb-3">
        <b-avatar
          :variant="patient.contentId ? 'white' : 'info'"
          :text="abbreviation"
          alt="User Image"
          size="120px"
          :src="viewFile(patient.contentId)"
          square
          rounded
        ></b-avatar>
      </div>
    </div>
    <div :class="!staticGrid ? 'col-4 col-lg-12' : 'col-12'">
      <p class="mb-0 narrow-spacing">
        DN: {{ patient.dn }} <CopyToClipboard :text="patient.dn" />
      </p>
      <p
        v-if="patient.existDn"
        class="mb-2 text-muted text-bold small-text narrow-spacing"
      >
        DN(เดิม): {{ patient.existDn }}
        <CopyToClipboard :text="patient.existDn" />
      </p>
      <div :class="!staticGrid ? 'd-block d-lg-none' : ''">
        <p class="mb-0 text-bold">{{ fullName }}</p>
        <p class="mb-2" v-if="patient.dob">
          อายุ: {{ calAge(patient.dob) }} ปี
        </p>
      </div>

      <div :class="!staticGrid ? 'd-none d-lg-block' : ''">
        <p v-if="patient.titleNameTh" class="mb-0">{{ patient.titleNameTh }}</p>
        <p class="mb-0 text-bold">{{ patient.firstNameTh }}</p>
        <p class="mb-0 text-bold">{{ patient.lastNameTh }}</p>
        <p class="mb-2" v-if="patient.dob">
          อายุ: {{ calAge(patient.dob) }} ปี
        </p>
      </div>
      <p v-if="patient.nickName" class="mb-2">({{ patient.nickName }})</p>
      <hr class="my-1" :class="!staticGrid ? 'd-none d-lg-block' : ''" />
      <div v-if="isManagerOrCounter">
        <p class="mb-2">โทร: {{ formatMobileNumber(patient.mobile) }}</p>
        <hr class="my-1" :class="!staticGrid ? 'd-none d-lg-block' : ''" />
      </div>
      <p v-if="isPatientDoctor" class="mb-2 narrow-spacing">
        แพทย์ประจำ: {{ getDoctorListBy(patient.doctorId, "uid").fullName }}
      </p>
    </div>
    <div :class="!staticGrid ? 'col-5 col-lg-12' : 'col-12'">
      <div class="mb-2">
        <PatientTagBadges :tag-ids="patient.tagIds" />
        <PatientTagPicker :patient="patient" @saved="onTagsSaved" />
      </div>
      <p
        v-for="f in healthLines"
        :key="f.label"
        class="mb-2"
        :class="{ 'text-danger': f.danger }"
      >
        {{ f.label }}: {{ f.text }}
      </p>
      <p v-if="patient.antithrombotic" class="mb-2">
        <span class="badge tag-antithrombotic">ต้านเกล็ดเลือด</span>
      </p>
      <p class="text-muted small-text mb-2">
        <HealthReviewedLine :patient="patient" />
        ·
        <a href="#" @click.prevent="$refs.healthHistory.show()"
          >ดูประวัติการแก้ไข</a
        >
        <HealthHistoryModal
          ref="healthHistory"
          :patient-id="patient.id"
          :patient="patient"
        />
      </p>
      <!--
        Mounted here, not in a modal-specific view: PatientProfile is the
        one component shared by PatientAnalysis.vue, ProfileCustomer.vue,
        Event.vue, NextVisit.vue, etc — putting the badge here means every
        one of those counter-facing modals gets it for free, which is the
        whole reason tier.view deliberately spans all branch roles rather
        than being management-only.
      -->
      <div v-if="canViewPatientTier" class="mb-2">
        <TierBadge
          :tier="patientTier"
          :status="patientTierState ? patientTierState.status : ''"
          :grace-until-dt="patientTierState ? patientTierState.graceUntilDt : null"
        />
      </div>
      <p v-if="patient.medicalInsuranceType" class="mb-2">
        สิทธิการเบิก: {{ displayInsuranceType(patient.medicalInsuranceType) }}
      </p>
     
      <p v-if="patient.note" class="mb-2">โน๊ต: {{ patient.note }}</p>
      <b-button v-if="buttonName" class="mb-3" block @click="buttonClick">
        {{ buttonName }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { formatPhoneForDisplay } from "@/utils/phoneUtils";
import { PERMISSIONS } from "@/constants/roles";

import CopyToClipboard from "./CopyToClipboard.vue";
import { ROLES } from "@/constants/user";
import TierBadge from "./tier/TierBadge.vue";
import PatientTagBadges from "@/components/aaci/PatientTagBadges.vue";
import PatientTagPicker from "@/components/aaci/PatientTagPicker.vue";
import HealthHistoryModal from "@/components/aaci/HealthHistoryModal.vue";
import HealthReviewedLine from "@/components/aaci/HealthReviewedLine.vue";
import { healthDisplay } from "@/utils/healthText";

export default {
  name: "PatientProfile",
  props: {
    patient: Object,
    buttonName: String,
    buttonClick: Function,
    staticGrid: Boolean,
  },
  components: {
    CopyToClipboard,
    TierBadge,
    PatientTagBadges,
    PatientTagPicker,
    HealthHistoryModal,
    HealthReviewedLine,
  },
  data() {
    return {
      ROLES,
      // Local to this instance, NOT read from moduleTier's getPatientTier
      // getter: that store slot is shared/overwritten by whichever fetch
      // ran last (see PatientTierPanel.vue's identical comment), and
      // PatientProfile mounts in several places at once (e.g. the photo AND
      // documents tabs of ProfileCustomer.vue each embed their own
      // instance for the same patient). Keeping the resolved dispatch value
      // locally means each instance shows its own patient's tier
      // regardless of what any other instance fetches concurrently.
      patientTierData: null, // { state, tier, events }
    };
  },
  computed: {
    ...mapGetters({
      viewFile: "moduleContent/viewFile",
      isAccessMenu: "moduleUser/getIsAccessMenu",
      getDoctorListBy: "moduleUser/getDoctorListBy",
      can: "moduleUser/can",
      getBranchInfo: "moduleUser/getBranchInfo",
    }),
    // tier.view is branch-scoped (the per-patient endpoint authorizes via
    // authByClinicURLBranchURL, see stores/tier.js's header comment), so
    // both clinicId and branchId go into the ctx, same as the
    // user.view.branch check in Sidebar.vue.
    healthLines() {
      const p = this.patient;
      const disease = healthDisplay(p.congenitalDisease);
      const allergy = healthDisplay(p.allergic);
      const meds = healthDisplay(p.medicationHistory);
      return [
        { label: "โรคประจำตัว", ...disease },
        { label: "แพ้ยา", ...allergy },
        { label: "ยาที่ใช้", text: meds.text, danger: false },
      ];
    },
    canViewPatientTier() {
      return this.can(PERMISSIONS.TIER_VIEW, {
        clinicId: this.getBranchInfo?.clinic?.id,
        branchId: this.getBranchInfo?.id,
      });
    },
    // null when the patient has no tier yet — TierBadge already renders
    // nothing in that case, so callers of PatientProfile don't need to know
    // this rule either.
    patientTier() {
      return this.patientTierData?.tier || null;
    },
    patientTierState() {
      return this.patientTierData?.state || null;
    },
    fullName() {
      if (this.patient) {
        var tilte = this.patient.titleNameTh
          ? this.patient.titleNameTh
          : this.patient.titleNameEn
          ? this.patient.titleNameEn
          : "";
        var firstName = this.patient.firstNameTh
          ? this.patient.firstNameTh
          : this.patient.firstNameEn
          ? this.patient.firstNameEn
          : "";
        var lastName = this.patient.lastNameTh
          ? this.patient.lastNameTh
          : this.patient.lastNameEn
          ? this.patient.lastNameEn
          : "";
        var fullName = [tilte, firstName, lastName].join(" ");
        return fullName;
      } else {
        return "";
      }
    },
    abbreviation() {
      if (
        this.patient &&
        (this.patient.firstNameEn || this.patient.lastNameEn)
      ) {
        var firstName = "";
        var lastName = "";
        if (this.patient.firstNameEn) {
          firstName = this.patient.firstNameEn.charAt(0);
        }
        if (this.patient.lastNameEn) {
          lastName = this.patient.lastNameEn.charAt(0);
        }
        return `${firstName}${lastName}`;
      } else {
        return "";
      }
    },

    isManagerOrCounter() {
      return this.isAccessMenu([ROLES.MANAGER, ROLES.COUNTER, ROLES.HEADCOUNTER]);
    },
    isPatientDoctor() {
      return (
        this.patient?.doctorId &&
        this.getDoctorListBy(this.patient.doctorId, "uid")
      );
    },
  },
  methods: {
    onTagsSaved(saved) {
      // The picker saves through modulePatient/savePatientTags (patientInfo is
      // untouched), so refresh the displayed object here and tell the parent.
      Object.assign(this.patient, { tagIds: saved.tagIds });
      this.$emit("patient-updated", saved);
    },
    formatMobileNumber(num) {
      return formatPhoneForDisplay(num);
    },
    calAge(dateString) {
      if (!dateString) return;
      const date = moment(dateString, "YYYY-MM-DD");
      return moment().diff(date, "years");
    },
    displayInsuranceType(type) {
      const insuranceLabels = {
        government_employee: "ข้าราชการ",
        self_pay: "ชำระค่าบริการเอง",
        social_security: "ประกันสังคม",
        state_enterprise: "รัฐวิสาหกิจ",
        universal_coverage: "สิทธิประกันสุขภาพถ้วนหน้า/บัตรทอง",
        other: "อื่นๆ",
      };
      return insuranceLabels[type] || type;
    },
    ...mapActions("moduleTier", ["getPatientTier"]),
    async loadPatientTier() {
      const clinicUrl = this.$route.params.clinicUrl;
      const branchUrl = this.$route.params.branchUrl;
      if (!this.canViewPatientTier || !this.patient?.id || !clinicUrl || !branchUrl) {
        this.patientTierData = null;
        return;
      }
      try {
        this.patientTierData = await this.getPatientTier({
          clinicUrl,
          branchUrl,
          patientId: this.patient.id,
        });
      } catch (error) {
        // A patient with no tier state yet, or a stale client-side
        // permission context, should not break the rest of the profile
        // card — fall back to showing no badge rather than surfacing an
        // error here.
        this.patientTierData = null;
      }
    },
  },
  watch: {
    "patient.id": {
      immediate: true,
      handler(id) {
        if (id) this.loadPatientTier();
        else this.patientTierData = null;
      },
    },
    // The permission context (moduleUser) can still be loading when this
    // component first mounts (e.g. a fresh page load lands straight on a
    // patient modal) — canViewPatientTier reads false until it resolves, so
    // the patient.id watch above would have already skipped the fetch.
    // Re-attempt once permission is actually confirmed, rather than leaving
    // the badge permanently absent for the rest of this component's life
    // just because of load-order timing. loadPatientTier() re-checks
    // canViewPatientTier itself, so this is a harmless no-op otherwise.
    canViewPatientTier(canView) {
      if (canView) this.loadPatientTier();
    },
  },
};
</script>

<style></style>
